/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import { Button } from 'components/ui';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import checkoutApi from 'services/api/checkout';
import Debounce from 'utils/Debounce';
import OrderUtils from 'utils/Order';
import checkoutUtils from 'utils/Checkout';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import basketUtils from 'utils/Basket';
import CheckoutBindings from 'analytics/bindingMethods/pages/checkout/CheckoutBindings';
import FreePaymentButton from 'components/FrictionlessCheckout/PlaceOrderButton/FreePaymentButton/FreePaymentButton';
import Venmo from 'utils/Venmo';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import ErrorsUtils from 'utils/Errors';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import UI from 'utils/UI';
import Storage from 'utils/localStorage/Storage';
import userUtils from 'utils/User';
import agentAwareUtils from 'utils/AgentAware';
import creditCardUtils from 'utils/CreditCard';
import CheckoutUtils from 'utils/Checkout';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { refreshCheckout } from 'services/Checkout';

const { checkForCheckoutSectionsCompleteness, getErrorStatesFromSubmitOrderAPI } = FrictionlessUtils;
import ErrorConstants from 'utils/ErrorConstants';

class PlaceOrderButton extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            isVenmoFlow: false,
            isProcessingOrder: false,
            orderError: null
        };
    }

    componentDidMount() {
        const isVenmoFlow = Venmo.isVenmoExpressFlow();

        this.setState({
            isVenmoFlow
        });

        const originalPlaceOrder = this.placeOrder;
        this.placeOrder = (e, data) => {
            if (this.props.placeOrderPreHook) {
                this.props.placeOrderPreHook(e).then(originalPlaceOrder);
            } else if (this.props.isSDUItemInBasket && !this.props.sduAcceptTerms) {
                this.triggerSDUModal();
            } else {
                originalPlaceOrder(data);
            }

            if (this.props.isAutoReplenishBasket && this.props.acceptAutoReplenish) {
                this.props.showPlaceOrderTermsModal(false);
            }
        };

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror, agent should see place order button disabled. However, certain roles should see enabled button only for card

            if (!agentAwareUtils.isAgentAuthorized(['3']) && !OrderUtils.isZeroCheckout()) {
                const updatedOrderDetails = {
                    ...this.props.orderDetails,
                    header: {
                        ...this.props.orderDetails?.header,
                        isComplete: false
                    }
                };
                this.props.updateOrder(updatedOrderDetails);
            }
        }

        this.placeOrder = Debounce.preventDoubleClick(this.placeOrder);
    }

    componentDidUpdate(prevProps) {
        const {
            isKlarnaSelected, isAfterpaySelected, isPazeSelected, isVenmoSelected, togglePlaceOrderDisabled
        } = this.props;

        if (
            (prevProps.isKlarnaSelected && !isKlarnaSelected) ||
            (prevProps.isAfterpaySelected && !isAfterpaySelected) ||
            (prevProps.isPazeSelected && !isPazeSelected) ||
            (prevProps.isVenmoSelected && !isVenmoSelected)
        ) {
            !basketUtils.isZeroCheckout() && togglePlaceOrderDisabled(true);
        }
    }

    handleVenmoClick = () => {
        Venmo.checkoutWithVenmo({ defaultPayment: this.props.defaultPaymentName, callback: this.onPlaceOrderSuccess });
    };

    isFreePaymentAvailable = () => {
        const {
            isKlarnaSelected,
            isKlarnaReady,
            isAfterpaySelected,
            isAfterpayReady,
            isPazeSelected,
            isPazeReady,
            isVenmoSelected,
            isVenmoReady,
            canCheckoutPaze
        } = this.props;
        const { isVenmoFlow } = this.state;

        const isFreePayment =
            (isKlarnaSelected && isKlarnaReady) ||
            (isAfterpaySelected && isAfterpayReady) ||
            (isPazeSelected && isPazeReady && canCheckoutPaze) ||
            (isVenmoSelected && isVenmoReady && !isVenmoFlow);

        return isFreePayment;
    };

    placeFreePaymentOrder = (event, freePaymentName) => {
        this.processPlaceOrderClickEvent(freePaymentName);
        const {
            getText, denialMessage, orderTotal, placeKlarnaOrder, placeAfterpayOrder, placePazeOrder, placeVenmoOrder
        } = this.props;

        const errorMessage = getText('authorizeErrorMessage', [freePaymentName]);

        const paymentActionsMapping = {
            Klarna: placeKlarnaOrder,
            Afterpay: placeAfterpayOrder,
            Paze: placePazeOrder,
            Venmo: placeVenmoOrder
        };

        const action = paymentActionsMapping[freePaymentName] || placePazeOrder;

        action({
            errorMessage,
            denialMessage,
            transactionAmount: basketUtils.removeCurrency(orderTotal)
        }).then(data => data && this.placeOrder(event, data));
    };

    processPlaceOrderClickEvent = freePaymentName => {
        const paymentName = freePaymentName.toLowerCase();
        const { defaultPaymentName, existingDefaultPayment } = this.props;
        const checkoutWithExistingDefaultPayment = paymentName === existingDefaultPayment;
        const actionInfo = `checkout:payment:continue with ${paymentName}`;
        const events = [anaConsts.Event.EVENT_71];

        if (defaultPaymentName || checkoutWithExistingDefaultPayment) {
            events.push('event252=1');
        }

        const eventData = {
            eventStrings: events,
            linkName: 'D=c55',
            actionInfo: actionInfo
        };

        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        if (mostRecentAsyncLoadEvent) {
            eventData.previousPage = mostRecentAsyncLoadEvent.eventInfo.attributes.previousPageName;
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
    };

    processPlaceOrderBtnClick = (e, freePaymentName) => {
        const { isVenmoSelected } = this.props;
        const { isVenmoFlow } = this.state;
        const errors = checkForCheckoutSectionsCompleteness(this.props.orderDetails, false, this.props.sectionThatNeedsToBeConfirmed);

        if (Object.keys(errors).length > 0) {
            this.props.setCheckoutSectionErrors(errors);
            UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });

            return;
        }

        isVenmoSelected && !isVenmoFlow ? this.handleVenmoClick() : this.placeFreePaymentOrder(e, freePaymentName);
    };

    triggerSDUModal = () => {
        this.props.showSDUAgreementModal({
            isOpen: true,
            isBopis: this.props.isBopis,
            canCheckoutPaze: this.props.canCheckoutPaze,
            isSDUItemInBasket: this.props.isSDUItemInBasket
        });
    };

    closeSDUModal = () => {
        typeof this.props.closeSDUModal === 'function' && this.props.closeSDUModal();
    };

    showCVVModal = creditCard => {
        const { orderDetails } = this.props;

        if (creditCardUtils.tokenMigrationFailed(creditCard)) {
            return false;
        }

        //Isolating all agent aware logic here
        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //For sephora mirror CVV will be forced based on below conditions:
            // Tier 1 and 2 Agents Required to enter CVV on ALL Paid transactions
            // Tier 1 and 2 Agents NOT Required to enter CVV on all Zero Dollar Transactions
            // Tier 3 Agents Never required to enter CVV on any Paid or Zero Dollar Orders
            const forceCVC = !OrderUtils.isZeroCheckout() && !agentAwareUtils.isAgentAuthorized(['3']);

            if (forceCVC) {
                Storage.session.setItem(LOCAL_STORAGE.EDIT_SEPHORA_CARD, false);
            }

            return forceCVC;
        }

        const ccComplete = creditCard.isCardInOrder && CheckoutUtils.isPaymentInOrderComplete() && OrderUtils.getCreditCardPaymentGroup(orderDetails);
        const isEfulfilledRewardWithCVVValidation = !OrderUtils.isShippableOrder(orderDetails) && OrderUtils.isZeroDollarOrderWithCVVValidation();
        const showCVCInput =
            !ccComplete && (!creditCard.isPreApproved || isEfulfilledRewardWithCVVValidation) && !OrderUtils.isSephoraTempCardType(creditCard);

        return showCVCInput;
    };

    placeOrder = (data = {}) => {
        const shouldValidateCVV = this.props.isCVVError && !this.props.orderDetails?.priceInfo?.paypalAmount && !this.isFreePaymentAvailable();
        const { pendingCreditCard } = this.props;

        if (pendingCreditCard && FrictionlessUtils.isFCReleaseFourEnabled()) {
            if (this.showCVVModal(pendingCreditCard)) {
                this.props.showConfirmCVVModal({
                    isOpen: true,
                    successCallback: () => this.placeOrder(data)
                });

                return;
            } else {
                const requestData = FrictionlessUtils.setRequestDataForCreditCardUpdate(pendingCreditCard);
                decorators
                    .withInterstice(checkoutApi.updateCreditCardOnOrder, INTERSTICE_DELAY_MS)(requestData, 'select')
                    .then(creditCardData => {
                        if (creditCardData) {
                            this.props.orderMerge('order', 'bankRewardsValidPaymentsMessage', data.bankRewardsValidPaymentsMessage);
                        }

                        this.props.swapPaypalToCreditCard();
                        refreshCheckout()().then(() => {
                            this.props.clearPendingCreditCard();
                        });
                    })
                    .catch(errorData => {
                        ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
                    });
            }
        }

        if (!this.props.orderDetails?.header?.isComplete || shouldValidateCVV || this.props.sectionThatNeedsToBeConfirmed) {
            // means order has an error somewhere
            const errors = checkForCheckoutSectionsCompleteness(this.props.orderDetails, shouldValidateCVV, this.props.sectionThatNeedsToBeConfirmed);

            if (Object.keys(errors).length > 0) {
                this.props.setCheckoutSectionErrors(errors);
                this.props.isSDUItemInBasket && this.closeSDUModal();
                UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });
                FrictionlessCheckoutBindings.errorTrackingBeforeAPICall(errors);

                return;
            }
        }

        this.props.isSDUItemInBasket && this.closeSDUModal();

        const isSavePayPal = OrderUtils.isPayPalSaveToAccountChecked();
        const params = {
            savePayPal: this.props.isPaypalPaymentEnabled ? isSavePayPal : false,
            originOfOrder: this.props.originOfOrder
        };

        if (this.props.defaultPaymentName) {
            params.defaultPayment = this.props.defaultPaymentName;
        }

        // If this item comes from data, it means this order was placed using Klarna
        if (data.authorization_token) {
            params.klarnaAuthorizationToken = data.authorization_token;
        }

        // If this item comes from data, it means this order was placed using Paze (placeOrder in Paze Utils)
        if (data.paze) {
            params.paze = data.paze;
        }

        // When customer journey started on affiliate links, some information is stored and it needs to be send to the purchase API
        const { linkshareSiteId, linkshareTime } = analyticsUtils.getAffiliateGatewayParams();

        if (linkshareSiteId !== '' && linkshareTime !== '') {
            params.linkshareInformation = {
                linkshareSiteId,
                linkshareTime
            };
        }

        //disable place order button just in case
        this.props.togglePlaceOrderDisabled(true);

        this.setState({
            isProcessingOrder: true,
            orderError: null
        });

        const birthdayGift = basketUtils.getBirthdayGift();

        decorators
            .withInterstice(
                checkoutApi.placeOrder,
                INTERSTICE_DELAY_MS
            )(params)
            .then(submittedDetails => {
                let isDefaultPaymentUsed;
                const { defaultPaymentMethodName, isDefaultPaymentBeingUsed, existingDefaultPayment } = this.props;
                const defaultPaymentNameToSend = defaultPaymentMethodName || existingDefaultPayment;

                if (this.isFreePaymentAvailable()) {
                    const freePaymentName = this.getFreePaymentName()?.toLowerCase();
                    const isUsingExistingDefaultPayment = freePaymentName === existingDefaultPayment;
                    isDefaultPaymentUsed = isUsingExistingDefaultPayment;
                } else {
                    isDefaultPaymentUsed = isDefaultPaymentBeingUsed;
                }

                FrictionlessCheckoutBindings.setAnalyticsForOrderConfirmation(defaultPaymentNameToSend, isDefaultPaymentUsed);

                FrictionlessCheckoutBindings.setPlaceOrderBtnClickanalytics({
                    isSuccess: true
                });

                if (birthdayGift) {
                    const { skuId, productId, brandName, productName } = birthdayGift.sku;
                    CheckoutBindings.birthdayGiftRedemption({ skuId, productId, brandName, productName });
                }

                if (this.props.isBopis) {
                    CheckoutBindings.placeBOPISOrder();
                } else {
                    CheckoutBindings.placeStandardOrder();
                }

                this.setState({
                    isProcessingOrder: false,
                    orderError: null
                });

                this.onPlaceOrderSuccess(submittedDetails);
            })
            .catch(this.onPlaceOrderFailure);
    };

    onPlaceOrderSuccess = submittedDetails => {
        this.props.isBopis && analyticsUtils.setNextPageData({ customData: { clickBopisPlaceOrder: true } });
        checkoutUtils.placeOrderSuccess(submittedDetails);

        if (this.props.showPreviouslyPurchasedPdp) {
            const profileId = userUtils.getProfileId();

            if (profileId) {
                const cacheKey = `purchaseHistory_${profileId}`;
                Storage.db.removeItem(cacheKey).catch(err => {
                    Sephora.logger.error('[Purchase History Cache Clear Error]:', err);
                });
            }
        }
    };

    onPlaceOrderFailure = errorData => {
        const errors = getErrorStatesFromSubmitOrderAPI(errorData);

        if (errorData.key === ErrorConstants.ERROR_CODES.KLARNA_NOT_AUTHORIZED) {
            return;
        }

        if (Object.keys(errors || {}).length > 0) {
            FrictionlessCheckoutBindings.setPlaceOrderBtnClickanalytics({
                isSuccess: false,
                hasErrorMessage: errorData?.errorMessages?.length > 0
            });
            UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });
            this.props.setCheckoutSectionErrors(errors);
        }

        this.setState({
            isProcessingOrder: false,
            orderError: errorData?.errorMessages?.[0]
        });
    };

    showGiftAddressWarning = () => {
        const placeOrderCallback = this.placeOrder;
        const recipientName = this.props?.digitalGiftMsg?.recipientName;

        return this.props.showGiftAddressWarningModal({ isOpen: true, placeOrderCallback, recipientName });
    };

    renderPaymentButton = () => {
        const freePaymentName = this.getFreePaymentName();
        const { isProcessingOrder } = this.state;
        const { isApplePayFlow, isAddressMisMatch, orderTotal } = this.props;

        const {
            block = true,
            children = this.props.placeOrder,
            isBopis,
            isAutoReplenishBasket,
            showPlaceOrderTermsModal,
            acceptAutoReplenish,
            isSDUItemInBasket
        } = this.props;

        const getButtonAriaLabel = () => {
            const baseLabel = `${this.props.placeOrderFor} ${orderTotal}`;

            if (isProcessingOrder) {
                return `${baseLabel}. ${this.props.processingOrder}`;
            }

            if (this.getDisabledState()) {
                return `${baseLabel}. ${this.props.buttonDisabled}`;
            }

            return baseLabel;
        };

        if (isApplePayFlow) {
            return (
                <ApplePayButton
                    isApplePayPayment={true}
                    isBopis={isBopis}
                    block={block}
                />
            );
        } else if (this.isFreePaymentAvailable()) {
            return (
                <FreePaymentButton
                    onClick={e => this.processPlaceOrderBtnClick(e, freePaymentName)}
                    block={block}
                    minWidth={[null, '15em']}
                    data-at={Sephora.debug.dataAt('place_order_btn')}
                    freePaymentName={freePaymentName}
                    label={this.props.placeFreePayment}
                    orderTotal={orderTotal}
                    withText={this.props.withText}
                    forProcessingText={this.props.forProcessingText}
                    logoAlt={this.props.logoAlt}
                />
            );
        } else {
            const buttonText = isProcessingOrder ? this.props.processingOrder : children;

            return (
                <Button
                    variant='special'
                    disabled={this.getDisabledState()}
                    onClick={
                        isAutoReplenishBasket && !acceptAutoReplenish
                            ? () => showPlaceOrderTermsModal(true, !isSDUItemInBasket)
                            : isAddressMisMatch
                                ? this.showGiftAddressWarning
                                : this.placeOrder
                    }
                    block={block}
                    minWidth={[null, '15em']}
                    data-at={Sephora.debug.dataAt('place_order_btn')}
                    aria-label={getButtonAriaLabel()}
                >
                    {buttonText}
                </Button>
            );
        }
    };

    getDisabledState = () => {
        const disabledButton = this.props.disabled;
        const {
            isKlarnaSelected, isKlarnaReady, isAfterpaySelected, isAfterpayReady, isPazeSelected, isPazeReady, isVenmoSelected, isVenmoReady
        } =
            this.props;

        return (
            disabledButton ||
            (isKlarnaSelected && !isKlarnaReady) ||
            (isAfterpaySelected && !isAfterpayReady) ||
            (isPazeSelected && !isPazeReady) ||
            (isVenmoSelected && !isVenmoReady)
        );
    };

    getFreePaymentName = () => {
        const { isKlarnaSelected, isPazeSelected, isAfterpaySelected } = this.props;

        if (isKlarnaSelected) {
            return 'Klarna';
        } else if (isPazeSelected) {
            return 'Paze';
        } else if (isAfterpaySelected) {
            return 'Afterpay';
        } else {
            return 'Venmo';
        }
    };

    render() {
        return <>{this.renderPaymentButton()}</>;
    }
}

export default wrapComponent(PlaceOrderButton, 'PlaceOrderButton', true);
